import React, { useState } from 'react';
import { 
  Container, 
  Typography, 
  Box, 
  Paper, 
  TextField, 
  Button, 
  Grid, 
  Divider,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  Stepper,
  Step,
  StepLabel,
  Tooltip,
  IconButton,
  InputAdornment
} from '@mui/material';
import CreditCardIcon from '@mui/icons-material/CreditCard';
import PaymentIcon from '@mui/icons-material/Payment';
import ReceiptIcon from '@mui/icons-material/Receipt';
import InfoIcon from '@mui/icons-material/Info';
import { Helmet } from 'react-helmet-async';
import PersonIcon from '@mui/icons-material/Person';
import EmailIcon from '@mui/icons-material/Email';
import HomeIcon from '@mui/icons-material/Home';
import LocationCityIcon from '@mui/icons-material/LocationCity';
import MarkunreadMailboxIcon from '@mui/icons-material/MarkunreadMailbox';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import NotesIcon from '@mui/icons-material/Notes';
import axios from 'axios';
import { loadStripe } from '@stripe/stripe-js';
import {
  Elements,
  CardElement,
  useStripe,
  useElements
} from '@stripe/react-stripe-js';

// Use a hardcoded Stripe test key for development
const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLIC_KEY);

// List of US states
const US_STATES = [
  { code: 'AL', name: 'Alabama' },
  { code: 'AK', name: 'Alaska' },
  { code: 'AZ', name: 'Arizona' },
  { code: 'AR', name: 'Arkansas' },
  { code: 'CA', name: 'California' },
  { code: 'CO', name: 'Colorado' },
  { code: 'CT', name: 'Connecticut' },
  { code: 'DE', name: 'Delaware' },
  { code: 'FL', name: 'Florida' },
  { code: 'GA', name: 'Georgia' },
  { code: 'HI', name: 'Hawaii' },
  { code: 'ID', name: 'Idaho' },
  { code: 'IL', name: 'Illinois' },
  { code: 'IN', name: 'Indiana' },
  { code: 'IA', name: 'Iowa' },
  { code: 'KS', name: 'Kansas' },
  { code: 'KY', name: 'Kentucky' },
  { code: 'LA', name: 'Louisiana' },
  { code: 'ME', name: 'Maine' },
  { code: 'MD', name: 'Maryland' },
  { code: 'MA', name: 'Massachusetts' },
  { code: 'MI', name: 'Michigan' },
  { code: 'MN', name: 'Minnesota' },
  { code: 'MS', name: 'Mississippi' },
  { code: 'MO', name: 'Missouri' },
  { code: 'MT', name: 'Montana' },
  { code: 'NE', name: 'Nebraska' },
  { code: 'NV', name: 'Nevada' },
  { code: 'NH', name: 'New Hampshire' },
  { code: 'NJ', name: 'New Jersey' },
  { code: 'NM', name: 'New Mexico' },
  { code: 'NY', name: 'New York' },
  { code: 'NC', name: 'North Carolina' },
  { code: 'ND', name: 'North Dakota' },
  { code: 'OH', name: 'Ohio' },
  { code: 'OK', name: 'Oklahoma' },
  { code: 'OR', name: 'Oregon' },
  { code: 'PA', name: 'Pennsylvania' },
  { code: 'RI', name: 'Rhode Island' },
  { code: 'SC', name: 'South Carolina' },
  { code: 'SD', name: 'South Dakota' },
  { code: 'TN', name: 'Tennessee' },
  { code: 'TX', name: 'Texas' },
  { code: 'UT', name: 'Utah' },
  { code: 'VT', name: 'Vermont' },
  { code: 'VA', name: 'Virginia' },
  { code: 'WA', name: 'Washington' },
  { code: 'WV', name: 'West Virginia' },
  { code: 'WI', name: 'Wisconsin' },
  { code: 'WY', name: 'Wyoming' },
  { code: 'DC', name: 'District of Columbia' }
];

// Stripe Card Element styling
const cardElementOptions = {
  style: {
    base: {
      fontSize: '16px',
      color: '#424770',
      '::placeholder': {
        color: '#aab7c4',
      },
    },
    invalid: {
      color: '#9e2146',
    },
  },
};

// Payment form component using Stripe Elements
const PaymentForm = ({ formData, handleInputChange, paymentAmount, customAmount, handleNext, activeStep, steps, handleBack }) => {
  const stripe = useStripe();
  const elements = useElements();
  const [isProcessing, setIsProcessing] = useState(false);
  const [paymentError, setPaymentError] = useState('');

  // Calculate processing fee and total
  const baseAmount = paymentAmount === 'standard' ? 135 : parseFloat(customAmount || 0);
  const processingFee = Math.round((baseAmount * 0.029 + 0.30) * 100) / 100; // 2.9% + $0.30
  const totalAmount = Math.round((baseAmount + processingFee) * 100) / 100;

  const handleSubmit = async (event) => {
    event.preventDefault();
    
    if (!stripe || !elements) {
      // Stripe.js has not loaded yet
      return;
    }

    setIsProcessing(true);
    setPaymentError('');

    try {
      const amount = totalAmount; // Use the total amount including fee
      
      // Combine address components
      const fullAddress = `${formData.streetAddress}, ${formData.city}, ${formData.state} ${formData.zipCode}`;
      
      // Create payment intent
      const { data: { clientSecret, paymentId } } = await axios.post('https://api.brynleighestates.com/api/payments/create-payment', {
        data: {
          amount: Number(amount), // Ensure amount is a number (will be converted to cents on server)
          address: fullAddress,
          streetAddress: formData.streetAddress,
          city: formData.city,
          state: formData.state,
          zipCode: formData.zipCode,
          cardholderName: formData.cardName,
          email: formData.email,
          notes: formData.notes,
          type: paymentAmount
        }
      }, {
        headers: {
          'Content-Type': 'application/json',
        }
      });

      // Confirm card payment
      const cardElement = elements.getElement(CardElement);
      const { error, paymentIntent } = await stripe.confirmCardPayment(clientSecret, {
        payment_method: {
          card: cardElement,
          billing_details: {
            name: formData.cardName,
            email: formData.email,
            address: {
              line1: formData.streetAddress,
              city: formData.city,
              state: formData.state,
              postal_code: formData.zipCode
            }
          }
        }
      });

      if (error) {
        throw new Error(error.message);
      }

      if (paymentIntent.status === 'succeeded') {
        try {
          // Update payment status in Strapi
          await axios.put(`https://api.brynleighestates.com/api/payments/${paymentId}`, {
            data: {
              paymentStatus: 'completed',
              stripePaymentId: paymentIntent.id
            }
          }, {
            headers: {
              'Content-Type': 'application/json',
            }
          });
        } catch (updateError) {
          console.error('Failed to update payment status:', updateError);
          console.error('Update error response:', updateError.response?.data);
          console.error('Update error status:', updateError.response?.status);
          // Continue anyway since payment was successful
        }

        handleNext();
      }
    } catch (error) {
      console.error('Payment failed:', error);
      let errorMessage = 'Payment failed. Please try again.';
      
      if (error.response) {
        // The request was made and the server responded with a status code outside of 2xx
        console.error('API Error Response:', error.response.data);
        
        // Show more detailed error message for debugging
        const errorDetails = error.response.data?.error?.details?.errors || [];
        if (errorDetails.length > 0) {
          console.error('Validation errors:', errorDetails);
        }
        
        errorMessage = `Server error: ${error.response.status} - ${error.response.data?.error?.message || error.message}`;
      } else if (error.request) {
        // The request was made but no response was received
        errorMessage = 'No response from server. Please check your connection.';
      } else {
        // Something else caused the error
        errorMessage = error.message;
      }
      
      setPaymentError(errorMessage);
    } finally {
      setIsProcessing(false);
    }
  };

  return (
    <form onSubmit={handleSubmit}>
      {paymentError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {paymentError}
        </Alert>
      )}
      <Grid container spacing={3}>
        <Grid item xs={12}>
          <Typography variant="subtitle2" gutterBottom>
            Property Address
          </Typography>
        </Grid>
        
        <Grid item xs={12}>
          <TextField
            required
            id="streetAddress"
            label="Street Address"
            fullWidth
            autoComplete="address-line1"
            value={formData.streetAddress}
            onChange={handleInputChange}
            placeholder="123 Brynleigh Dr"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <HomeIcon />
                </InputAdornment>
              ),
            }}
          />
        </Grid>
        
        <Grid item xs={12} sm={6}>
          <TextField
            required
            id="city"
            label="City"
            fullWidth
            autoComplete="address-level2"
            value={formData.city}
            onChange={handleInputChange}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <LocationCityIcon />
                </InputAdornment>
              ),
            }}
          />
        </Grid>
        
        <Grid item xs={6} sm={3}>
          <FormControl fullWidth required>
            <InputLabel id="state-label">State</InputLabel>
            <Select
              labelId="state-label"
              id="state"
              name="state"
              value={formData.state}
              label="State"
              onChange={handleInputChange}
              MenuProps={{
                PaperProps: {
                  style: {
                    maxHeight: 224,
                  },
                },
              }}
            >
              {US_STATES.map((state) => (
                <MenuItem key={state.code} value={state.code}>
                  {state.code}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
        
        <Grid item xs={6} sm={3}>
          <TextField
            required
            id="zipCode"
            label="Zip Code"
            fullWidth
            autoComplete="postal-code"
            value={formData.zipCode}
            onChange={handleInputChange}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <MarkunreadMailboxIcon />
                </InputAdornment>
              ),
            }}
          />
        </Grid>
        
        <Grid item xs={12}>
          <TextField
            required
            id="email"
            label="Email Address"
            type="email"
            fullWidth
            autoComplete="email"
            value={formData.email}
            onChange={handleInputChange}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <EmailIcon />
                </InputAdornment>
              ),
            }}
          />
        </Grid>
        
        <Grid item xs={12} sx={{ mt: 2 }}>
          <Divider />
        </Grid>
        
        <Grid item xs={12}>
          <Typography variant="subtitle2" gutterBottom>
            Payment Information
          </Typography>
        </Grid>
        
        <Grid item xs={12}>
          <TextField
            required
            id="cardName"
            label="Name on Card"
            fullWidth
            autoComplete="cc-name"
            value={formData.cardName}
            onChange={handleInputChange}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <PersonIcon />
                </InputAdornment>
              ),
            }}
          />
        </Grid>
        
        {paymentAmount === 'custom' && (
          <Grid item xs={12}>
            <TextField
              id="notes"
              label="Payment Notes (Optional)"
              fullWidth
              multiline
              rows={2}
              value={formData.notes}
              onChange={handleInputChange}
              placeholder="Specify reason for custom payment amount"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <NotesIcon />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
        )}
        
        <Grid item xs={12}>
          <Box sx={{ border: '1px solid #e0e0e0', p: 2, borderRadius: 1 }}>
            <CardElement options={cardElementOptions} />
          </Box>
        </Grid>
        
        <Grid item xs={12} sx={{ mt: 2 }}>
          <Alert severity="info" icon={<CreditCardIcon />}>
            <Typography variant="subtitle2">Secure Payment Processing</Typography>
            <Typography variant="body2">
              Your payment information is encrypted and processed securely through Stripe. 
              We never store your credit card details on our servers.
            </Typography>
            <Typography variant="body2" sx={{ mt: 1 }}>
              A processing fee of 2.9% + $0.30 (${processingFee.toFixed(2)}) will be added to your payment.
              <Tooltip 
                title={
                  <React.Fragment>
                    <Typography variant="body2" fontWeight="bold">Processing Fee Breakdown:</Typography>
                    <Typography variant="body2">• Credit Card Fee: 2.9% of transaction amount</Typography>
                    <Typography variant="body2">• Fixed Transaction Fee: $0.30 per transaction</Typography>
                    <Typography variant="body2" fontStyle="italic" sx={{ mt: 1 }}>
                      These fees cover the costs charged by payment processors to securely handle credit card transactions.
                    </Typography>
                  </React.Fragment>
                } 
                arrow
              >
                <IconButton size="small" sx={{ ml: 1, verticalAlign: 'middle' }}>
                  <InfoIcon fontSize="small" color="info" />
                </IconButton>
              </Tooltip>
            </Typography>
          </Alert>
        </Grid>
      </Grid>

      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 3 }}>
        {activeStep !== 0 && (
          <Button onClick={handleBack} sx={{ mr: 1 }} disabled={isProcessing}>
            Back
          </Button>
        )}
        <Button
          type="submit"
          variant="contained"
          color="primary"
          disabled={isProcessing || !stripe}
          startIcon={activeStep === steps.length - 2 ? <PaymentIcon /> : null}
        >
          {isProcessing ? 'Processing...' : (activeStep === steps.length - 2 ? 'Pay Now' : 'Next')}
        </Button>
      </Box>
    </form>
  );
};

const PayHOA = () => {
  const [activeStep, setActiveStep] = useState(0);
  const [paymentAmount, setPaymentAmount] = useState('standard');
  const [customAmount, setCustomAmount] = useState('');
  const [formData, setFormData] = useState({
    streetAddress: '',
    city: '',
    state: '',
    zipCode: '',
    cardName: '',
    email: '',
    notes: '',
  });
  
  const steps = ['Payment Details', 'Review', 'Confirmation'];
  
  // Calculate processing fee and total
  const baseAmount = paymentAmount === 'standard' ? 135 : parseFloat(customAmount || 0);
  const processingFee = Math.round((baseAmount * 0.029 + 0.30) * 100) / 100; // 2.9% + $0.30
  const totalAmount = Math.round((baseAmount + processingFee) * 100) / 100;

  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.id || e.target.name]: e.target.value
    });
  };

  const handleNext = () => {
    setActiveStep((prevStep) => prevStep + 1);
  };
  
  const handleBack = () => {
    setActiveStep((prevStep) => prevStep - 1);
  };
  
  const handlePaymentAmountChange = (event) => {
    setPaymentAmount(event.target.value);
  };

  const handleCustomAmountChange = (event) => {
    setCustomAmount(event.target.value);
  };
  
  const getStepContent = (step) => {
    switch (step) {
      case 0:
        return (
          <Box>
            <Typography variant="h6" gutterBottom>
              Payment Details
            </Typography>
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <FormControl fullWidth>
                  <InputLabel id="payment-amount-label">Payment Amount</InputLabel>
                  <Select
                    labelId="payment-amount-label"
                    id="payment-amount"
                    value={paymentAmount}
                    label="Payment Amount"
                    onChange={handlePaymentAmountChange}
                  >
                    <MenuItem value="standard">Standard HOA Fee ($135)</MenuItem>
                    <MenuItem value="custom">Custom Amount</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              
              {paymentAmount === 'custom' && (
                <Grid item xs={12}>
                  <TextField
                    required
                    id="custom-amount"
                    label="Custom Amount"
                    fullWidth
                    type="number"
                    value={customAmount}
                    onChange={handleCustomAmountChange}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <AttachMoneyIcon />
                        </InputAdornment>
                      ),
                    }}
                  />
                </Grid>
              )}
              
              <Grid item xs={12}>
                <Elements stripe={stripePromise}>
                  <PaymentForm 
                    formData={formData}
                    handleInputChange={handleInputChange}
                    paymentAmount={paymentAmount}
                    customAmount={customAmount}
                    handleNext={handleNext}
                    activeStep={activeStep}
                    steps={steps}
                    handleBack={handleBack}
                  />
                </Elements>
              </Grid>
            </Grid>
          </Box>
        );
      case 1:
        return (
          <Box>
            <Typography variant="h6" gutterBottom>
              Review Your Payment
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <Paper variant="outlined" sx={{ p: 2 }}>
                  <Typography variant="subtitle1" gutterBottom>
                    Payment Summary
                  </Typography>
                  <Box display="flex" justifyContent="space-between" mb={1}>
                    <Typography variant="body1">Payment Type:</Typography>
                    <Typography variant="body1">
                      {paymentAmount === 'standard' && 'Standard HOA Fee'}
                      {paymentAmount === 'custom' && 'Custom Payment'}
                    </Typography>
                  </Box>
                  <Box display="flex" justifyContent="space-between" mb={1}>
                    <Typography variant="body1">Base Amount:</Typography>
                    <Typography variant="body1">
                      {paymentAmount === 'standard' && '$135.00'}
                      {paymentAmount === 'custom' && `$${parseFloat(customAmount || 0).toFixed(2)}`}
                    </Typography>
                  </Box>
                  <Box display="flex" justifyContent="space-between" mb={1}>
                    <Typography variant="body1">Property Address:</Typography>
                    <Typography variant="body1" sx={{ textAlign: 'right' }}>
                      {formData.streetAddress}<br />
                      {formData.city}, {formData.state} {formData.zipCode}
                    </Typography>
                  </Box>
                  <Box display="flex" justifyContent="space-between" mb={1}>
                    <Typography variant="body1">Email:</Typography>
                    <Typography variant="body1">{formData.email}</Typography>
                  </Box>
                  {paymentAmount === 'custom' && formData.notes && (
                    <Box mb={1}>
                      <Typography variant="body1">Notes:</Typography>
                      <Typography variant="body2" sx={{ mt: 0.5, p: 1, bgcolor: 'background.default', borderRadius: 1 }}>
                        {formData.notes}
                      </Typography>
                    </Box>
                  )}
                  <Box display="flex" justifyContent="space-between" mb={1}>
                    <Typography variant="body1" sx={{ display: 'flex', alignItems: 'center' }}>
                      Processing Fee (2.9% + $0.30):
                      <Tooltip 
                        title={
                          <React.Fragment>
                            <Typography variant="body2" fontWeight="bold">Processing Fee Breakdown:</Typography>
                            <Typography variant="body2">• Credit Card Fee: 2.9% of transaction amount</Typography>
                            <Typography variant="body2">• Fixed Transaction Fee: $0.30 per transaction</Typography>
                            <Typography variant="body2" fontStyle="italic" sx={{ mt: 1 }}>
                              These fees cover the costs charged by payment processors to securely handle credit card transactions.
                            </Typography>
                          </React.Fragment>
                        } 
                        arrow
                      >
                        <IconButton size="small" sx={{ ml: 1 }}>
                          <InfoIcon fontSize="small" color="info" />
                        </IconButton>
                      </Tooltip>
                    </Typography>
                    <Typography variant="body1">${processingFee.toFixed(2)}</Typography>
                  </Box>
                  <Divider sx={{ my: 1 }} />
                  <Box display="flex" justifyContent="space-between">
                    <Typography variant="subtitle1">Total:</Typography>
                    <Typography variant="subtitle1">
                      ${totalAmount.toFixed(2)}
                    </Typography>
                  </Box>
                </Paper>
              </Grid>
              <Grid item xs={12}>
                <Alert severity="info">
                  This is a secure payment processed by Stripe. Your card information is encrypted and never stored on our servers.
                </Alert>
              </Grid>
              
              <Grid item xs={12} sx={{ mt: 1 }}>
                <Typography variant="body2" color="text.secondary" align="center">
                  Brynleigh Estates uses industry-standard security measures to protect your information.
                  All transactions are SSL encrypted and comply with PCI DSS requirements.
                </Typography>
              </Grid>

              <Grid item xs={12} sx={{ mt: 2 }}>
                <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                  <Button onClick={handleBack} sx={{ mr: 1 }}>
                    Back
                  </Button>
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={handleNext}
                    startIcon={<PaymentIcon />}
                  >
                    Process Payment
                  </Button>
                </Box>
              </Grid>
            </Grid>
          </Box>
        );
      case 2:
        return (
          <Box textAlign="center">
            <Box sx={{ mb: 3, mt: 2 }}>
              <ReceiptIcon color="success" sx={{ fontSize: 60 }} />
            </Box>
            <Typography variant="h5" gutterBottom>
              Thank You For Your Payment!
            </Typography>
            <Typography variant="subtitle1" gutterBottom>
              Your HOA dues payment has been successfully processed.
            </Typography>
            <Typography variant="body1" paragraph>
              A receipt has been sent to <strong>{formData.email}</strong>. If you have any questions about your payment, please contact the HOA board.
            </Typography>
            <Button 
              variant="outlined" 
              color="primary" 
              sx={{ mt: 2 }}
              onClick={() => {
                setActiveStep(0);
                setFormData({
                  streetAddress: '',
                  city: '',
                  state: '',
                  zipCode: '',
                  cardName: '',
                  email: '',
                  notes: '',
                });
                setPaymentAmount('standard');
                setCustomAmount('');
              }}
            >
              Make Another Payment
            </Button>
          </Box>
        );
      default:
        return 'Unknown step';
    }
  };

  return (
    <>
      <Helmet>
        <title>Pay HOA Dues Online | Brynleigh Estates</title>
        <meta name="description" content="Securely pay your Brynleigh Estates HOA dues online. The annual HOA fee is $135 and covers maintenance of common areas and community events." />
        <link rel="canonical" href="https://brynleighestates.com/pay-hoa" />
        
        {/* Open Graph / Facebook */}
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://brynleighestates.com/pay-hoa" />
        <meta property="og:title" content="Pay HOA Dues Online | Brynleigh Estates" />
        <meta property="og:description" content="Securely pay your Brynleigh Estates HOA dues online. The annual HOA fee is $135 and covers maintenance of common areas and community events." />
        <meta property="og:image" content="https://brynleighestates.com/images/brynleigh-estates-social-logo.jpeg" />
        
        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Pay HOA Dues Online | Brynleigh Estates" />
        <meta name="twitter:description" content="Securely pay your Brynleigh Estates HOA dues online." />
        <meta name="twitter:image" content="https://brynleighestates.com/images/brynleigh-estates-social-logo.jpeg" />
      </Helmet>
      <Container maxWidth="md" sx={{ py: 4 }}>
        <Paper elevation={3} sx={{ p: { xs: 2, md: 4 } }}>
          <Box textAlign="center" mb={4}>
            <Typography variant="h4" component="h1" gutterBottom>
              Brynleigh Estates HOA Payment
            </Typography>
            <Typography variant="subtitle1" color="text.secondary">
              Securely pay your annual dues or special assessments online
            </Typography>
            
            <Box display="flex" alignItems="center" justifyContent="center" mt={1}>
              <Box 
                sx={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  bgcolor: 'success.light', 
                  color: 'success.contrastText',
                  px: 1.5,
                  py: 0.5,
                  borderRadius: 1,
                  fontSize: '0.75rem'
                }}
              >
                <CreditCardIcon fontSize="small" sx={{ mr: 0.5 }} />
                Secure Payment
              </Box>
            </Box>
          </Box>
          
          <Stepper activeStep={activeStep} sx={{ mb: 4 }}>
            {steps.map((label) => (
              <Step key={label}>
                <StepLabel>{label}</StepLabel>
              </Step>
            ))}
          </Stepper>
          
          {getStepContent(activeStep)}
          
        </Paper>
        
        <Box mt={4}>
          <Paper elevation={1} sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              About HOA Dues
            </Typography>
            <Typography variant="body1" paragraph>
              HOA dues are used to maintain common areas, fund community events, and ensure Brynleigh Estates remains a premier residential community. The standard annual HOA fee is $135.
            </Typography>
            <Typography variant="body1" paragraph>
              Online payments are subject to a processing fee of 2.9% + $0.30 to cover credit card transaction costs. This fee is added to your payment total.
              <Tooltip 
                title={
                  <React.Fragment>
                    <Typography variant="body2" fontWeight="bold">Processing Fee Breakdown:</Typography>
                    <Typography variant="body2">• Credit Card Fee: 2.9% of transaction amount</Typography>
                    <Typography variant="body2">• Fixed Transaction Fee: $0.30 per transaction</Typography>
                    <Typography variant="body2" fontStyle="italic" sx={{ mt: 1 }}>
                      These fees cover the costs charged by payment processors to securely handle credit card transactions.
                    </Typography>
                  </React.Fragment>
                } 
                arrow
              >
                <IconButton size="small" sx={{ ml: 1, verticalAlign: 'middle' }}>
                  <InfoIcon fontSize="small" color="info" />
                </IconButton>
              </Tooltip>
            </Typography>
            <Typography variant="body1" paragraph>
              If you have questions about your HOA dues or need assistance with payment, please contact our treasurer or any board member.
            </Typography>
            
            <Box sx={{ mt: 2, p: 2, bgcolor: 'background.paper', borderRadius: 1, border: '1px solid', borderColor: 'divider' }}>
              <Typography variant="subtitle1" fontWeight="medium" gutterBottom>
                Treasurer Contact Information
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <PersonIcon fontSize="small" sx={{ mr: 1, color: 'primary.main' }} />
                <Typography variant="body2">
                  Daniel E Hermany Jr. - Treasurer
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <EmailIcon fontSize="small" sx={{ mr: 1, color: 'primary.main' }} />
                <Typography variant="body2">
                  <a href="mailto:treasurer@brynleighestates.com">treasurer@brynleighestates.com</a>
                </Typography>
              </Box>
            </Box>
          </Paper>
        </Box>
      </Container>
    </>
  );
};

export default PayHOA; 